import { ChangeLanguageModal } from '@mindly/ui-components';
import i18n from 'src/i18n';
import { LanguageWorkInProgressModalFeature } from '../LanguageWorkInProgressModalFeature';
import { useCallback, useState } from 'react';
import useLangs from 'src/context/LangsContext';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'src/shared';

type ChangeLanguageModalWithLanguageWorkInProgressFeatureProps = {
  isOpen: boolean;
  closeModal: () => void;
  onSelect?: (language: string) => void;
};

export const ChangeLanguageModalWithLanguageWorkInProgressFeature = ({
  isOpen,
  closeModal,
  onSelect,
}: ChangeLanguageModalWithLanguageWorkInProgressFeatureProps) => {
  const { trackPredefinedEvent } = useAnalytics();
  const { t } = useTranslation('auth');
  const { changeLocale, availableLanguages, defaultLocale } = useLangs();

  const [isLanguageWorkInProgressModalOpen, setIsLanguageWorkInProgressModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);

  const handleOnSelect = useCallback(
    (l) => {
      trackPredefinedEvent('LOGIN_SELECT_LANGUAGE_SELECTED', { language: l });
      const isWorkInProgress = availableLanguages.find((lang) => lang.code === l)?.isWorkInProgress;
      if (isWorkInProgress) {
        setSelectedLanguage(l);
        setIsLanguageWorkInProgressModalOpen(true);

        closeModal();
        changeLocale(defaultLocale);
        return;
      }

      const selectedLocale = localStorage.getItem('mindlySelectedLocale');
      if (!selectedLocale) {
        localStorage.setItem('mindlySelectedLocale', l);
      }

      changeLocale(l);
      closeModal();
      onSelect?.(l);
    },
    [availableLanguages]
  );

  const handleOnModalClose = useCallback(() => {
    const selectedLocale = localStorage.getItem('mindlySelectedLocale');
    if (!selectedLocale) {
      localStorage.setItem('mindlySelectedLocale', i18n.language);
    }
    closeModal();
  }, [closeModal]);

  const handleOnChangeLanguageModalDidPresent = useCallback(() => {
    trackPredefinedEvent('LOGIN_SELECT_LANGUAGE_VIEWED');
  }, [trackPredefinedEvent]);

  const handleOnLanguageWorkInProgressModalDidPresent = useCallback(() => {
    trackPredefinedEvent('LOGIN_LANGUAGE_UNSUPPORTED_VIEWED');
  }, [trackPredefinedEvent]);

  return (
    <>
      <ChangeLanguageModal
        languages={availableLanguages}
        title={t('auth.language.title')}
        selected={i18n.language}
        isOpen={isOpen}
        onSelect={handleOnSelect}
        onModalClose={handleOnModalClose}
        onDidPresent={handleOnChangeLanguageModalDidPresent}
      />
      <LanguageWorkInProgressModalFeature
        isOpen={isLanguageWorkInProgressModalOpen}
        closeModal={() => setIsLanguageWorkInProgressModalOpen(false)}
        selectedLanguage={selectedLanguage}
        onDidPresent={handleOnLanguageWorkInProgressModalDidPresent}
      />
    </>
  );
};
